import React, { useMemo } from 'react';
import { marked } from 'marked';
import { clsx } from 'clsx';
import { sanitizeHtml } from '../../lib/sanitize';

interface Props {
  value: string | null | undefined;
  className?: string;
  /** Render external links with target="_blank" + rel. Defaults to true. */
  externalLinks?: boolean;
  /** Allow <img> in the rendered output. Defaults to true. Pass false for
   *  LLM/AI-chat output to prevent image-beacon PII exfiltration. */
  allowImages?: boolean;
  /** Strip <a> to plain text. Defaults to false. Pass true for LLM/AI-chat
   *  output so a prompt-injected link can't become a click-gated exfil channel. */
  neutralizeLinks?: boolean;
  /** 'chat': compact spacing for narrow chat bubbles; code blocks and tables
   *  scroll inside the bubble instead of widening it. */
  variant?: 'default' | 'chat';
}

// GFM-flavored + linebreaks so a single newline in the editor becomes a <br>,
// matching what users expect from textarea input.
marked.setOptions({ gfm: true, breaks: true });

const PROSE_CLS =
  // Headings — H1 reserved for page title; markdown ## maps to a visible subheading.
  '[&_h1]:text-3xl [&_h1]:font-bold [&_h1]:text-slate-900 [&_h1]:mt-6 [&_h1]:mb-3 ' +
  '[&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:mt-6 [&_h2]:mb-3 ' +
  '[&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-slate-900 [&_h3]:mt-5 [&_h3]:mb-2 ' +
  // Lists
  '[&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-3 ' +
  '[&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-3 ' +
  '[&_li]:mb-1.5 ' +
  // Inline + paragraph
  '[&_p]:mb-3 [&_p:last-child]:mb-0 ' +
  '[&_strong]:font-bold ' +
  '[&_em]:italic ' +
  '[&_a]:underline [&_a]:font-medium ' +
  '[&_a]:text-[color:var(--agency-primary,theme(colors.indigo.600))] ' +
  '[&_a:hover]:opacity-80 ' +
  '[&_code]:font-mono [&_code]:text-sm [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:bg-slate-100 ' +
  '[&_blockquote]:border-l-4 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-slate-500 [&_blockquote]:my-3 ' +
  '[&_hr]:my-6 [&_hr]:border-slate-200';

// `!` beats PROSE_CLS — both sets target the same elements.
const CHAT_CLS =
  '[&_ul]:pl-4! [&_ol]:pl-4! [&_li]:mb-0.5! [&_p]:mb-2! [&_p:last-child]:mb-0! ' +
  '[&_pre]:max-w-full [&_pre]:overflow-x-auto [&_pre]:text-xs [&_pre]:my-2 ' +
  // Tables keep normal wrapping (the bubble's overflow-wrap:anywhere would
  // squeeze cells letter by letter) and scroll sideways instead.
  '[&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto [&_table]:text-xs [&_table]:my-2 [&_table]:[overflow-wrap:normal] ' +
  '[&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_td]:px-2 [&_td]:py-1';

export const MarkdownView: React.FC<Props> = ({ value, className, externalLinks = true, allowImages = true, neutralizeLinks = false, variant = 'default' }) => {
  const html = useMemo(() => {
    if (!value || !value.trim()) return '';
    // `marked.parse` is sync when no async extensions are registered.
    const rendered = marked.parse(value) as string;
    // The chat variant renders model output: strict chat profile (no class/id/target).
    let safe = sanitizeHtml(rendered, { allowImages, profile: variant === 'chat' ? 'chat' : undefined });
    if (neutralizeLinks) {
      // Strip anchors to their text — a prompt-injected URL can't be clicked.
      safe = safe.replace(/<a\b[^>]*>([\s\S]*?)<\/a>/gi, '$1');
    } else if (externalLinks) {
      // Patch http(s) anchors to open in a new tab safely. Internal/relative links untouched.
      safe = safe.replace(/<a\s+([^>]*?)href="(https?:[^"]+)"([^>]*)>/gi,
        '<a $1href="$2"$3 target="_blank" rel="noopener noreferrer">');
    }
    return safe;
  }, [value, externalLinks, allowImages, neutralizeLinks, variant]);

  if (!html) return null;

  return (
    <div
      className={clsx(PROSE_CLS, variant === 'chat' && CHAT_CLS, className)}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};
